import { Spine, type SkeletonData, type TrackEntry } from "@esotericsoftware/spine-pixi-v8";
import type { PresentationTaskRunner } from "../../runner/PresentationTaskRunner";
import type { SymbolView } from "../../view/grid/SymbolView";
import { applySpineClipMixes, type SpineClipMixSpec } from "../../spine/SpineClipMix";
import type { SpineDriver } from "../../spine/SpineDriver";

/** One per-symbol Spine entry: the skeleton + how to seat it over the cell. */
export interface SymbolSpineSource {
  /** Bundle-loaded skeleton data for this symbol. */
  skeletonData: SkeletonData;
  /**
   * Fine-tune MULTIPLIER (default 1). Spine art authored 1:1 with the sprite natives shares the
   * cell's content parent, so scale 1 renders it at the sprite's exact on-screen size.
   */
  scale: number;
  /** Optional pixel offset in cell space, y-down (default 0,0). */
  offsetX?: number;
  offsetY?: number;
  /**
   * Whether the symbol rests on the host's idle loop. False: the loop never plays, even when the
   * skeleton carries it — a resting cell stays a plain sprite, and a land or win clip holds its last
   * frame instead of rolling into the loop.
   */
  idle: boolean;
}

/** Used when a clip plays but the runtime reports no length — never treated as "not handled". */
const FALLBACK_CLIP_SECONDS = 0.4;

interface HostedCell {
  spine: Spine;
  symbolId: string;
  /** The sprite hidden under the spine, restored on detach. */
  sprite: { visible: boolean; destroyed: boolean } | null;
}

/**
 * THE cell's Spine skeleton — one instance per hosted cell, for its whole life on the grid.
 *
 * Every symbol animation (`in` land, `idle` ambient, `win` flourish, `out` destruction) plays on
 * this ONE instance, so there are no handoffs between effects (each was a visible seam or a lifetime
 * bug) and clip changes can CROSSFADE (a mix lives on the per-instance `AnimationStateData`).
 *
 * Lifecycle rides the grid's POOL hooks, not round events:
 *   - `setSymbolIdHook` → `onViewBound(view, id, transient, pendingLand)` — a RESTING bind attaches
 *     (and starts the idle loop); a TRANSIENT bind (a scrolling drum lane) detaches.
 *   - `setSymbolReleaseHook` → `detach(view)` — one teardown for every effect. Attaching is also on
 *     demand: `play()` attaches if needed.
 *
 * TIME: ambient clips run at `timeScale = 1` (chrome); every other clip runs at
 * `runner.getTimeScale()` so it speeds up with turbo. The speed is set on the skeleton's state when
 * a clip starts, so the crossfade into the clip keeps pace. The idle that a land or win clip queues
 * divides that speed back out on its own track entry, so it runs at 1 whatever speed it follows.
 * Instances are ticked by the injected `SpineDriver` (presentation ticker), so the dev pause
 * freezes them like everything else.
 *
 * Symbols with no entry in `sourcesById`, and clips a given skeleton doesn't carry, are handled by
 * returning "nothing happened" (null / 0 seconds) — callers fall back to their sprite behaviour.
 */
export class SymbolSpineHost {
  private readonly sourcesById: ReadonlyMap<string, SymbolSpineSource>;
  private readonly clipMix: SpineClipMixSpec | null;
  private readonly taskRunner: PresentationTaskRunner;
  private readonly driver: SpineDriver;
  private readonly idleClipName: string | null;
  private readonly landClipName: string | null;
  /** Symbols bound without the pre-land pose (their land clip never plays). */
  private readonly landSkipSymbolIds: ReadonlySet<string>;
  private readonly ambientClips: ReadonlySet<string>;
  /** view → its live skeleton. */
  private readonly hosted: Map<SymbolView, HostedCell> = new Map();
  /** Free instances per symbolId (detached, tracks cleared, reset to setup pose). */
  private readonly pool: Map<string, Spine[]> = new Map();
  /** `${symbolId}:${clip}` → does the skeleton carry it. */
  private readonly clipPresenceCache: Map<string, boolean> = new Map();
  /** `${symbolId}:${clip}` → known duration seconds. */
  private readonly clipSecondsCache: Map<string, number> = new Map();

  public constructor(input: {
    /** symbolId → skeleton + seat. Symbols without an entry are never hosted. */
    sourcesById: ReadonlyMap<string, SymbolSpineSource>;
    taskRunner: PresentationTaskRunner;
    /** Ticks the hosted instances from the presentation ticker. */
    driver: SpineDriver;
    /** Ambient loop started on attach (e.g. "idle"). Null → attach leaves the skeleton idle-less. */
    idleClipName?: string | null;
    /**
     * The land clip (e.g. "in"). A cell bound as `pendingLand` is POSED on its first frame, so the
     * symbol arrives already looking like the animation that is about to play.
     */
    landClipName?: string | null;
    /**
     * Symbols that never land-animate: they are bound plain, without the pre-land pose, so they
     * cannot be left frozen on a first frame no one plays. The land EFFECT must decline the same
     * ids — the host only decides the pose.
     */
    landSkipSymbolIds?: readonly string[] | null;
    /** Clips that are chrome, not round presentation — run at timeScale 1 (e.g. ["idle"]). */
    ambientClips?: readonly string[];
    /** Crossfade table installed on every hosted skeleton. */
    clipMix?: SpineClipMixSpec | null;
  }) {
    this.sourcesById = input.sourcesById;
    this.taskRunner = input.taskRunner;
    this.driver = input.driver;
    this.idleClipName = input.idleClipName ?? null;
    this.landClipName = input.landClipName ?? null;
    this.landSkipSymbolIds = new Set(input.landSkipSymbolIds ?? []);
    this.ambientClips = new Set(input.ambientClips ?? []);
    this.clipMix = input.clipMix ?? null;
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────────

  /** Wire to `setSymbolIdHook`. Resting bind → host; transient (drum lane) → un-host. */
  public onViewBound(view: SymbolView, symbolId: string, transient?: boolean, pendingLand?: boolean): void {
    if (transient) {
      this.detach(view);
      return;
    }
    const poses = pendingLand && !this.landSkipSymbolIds.has(symbolId);
    this.attach(view, symbolId, poses ? { poseClipName: this.landClipName } : undefined);
  }

  /**
   * Host this view's symbol and start its idle loop. Returns the instance (or null when the symbol
   * has none). A cell already hosting this same symbol is left ALONE — its animation keeps running.
   */
  public attach(
    view: SymbolView,
    symbolId: string,
    opts?: {
      /** Start the ambient loop (default true). False when the caller sets its own clip next. */
      startIdle?: boolean;
      /** Hold this clip's FIRST FRAME, frozen (timeScale 0), instead of idling. */
      poseClipName?: string | null;
    },
  ): Spine | null {
    const source = this.sourcesById.get(symbolId);
    const parent = view.contentNode ?? view.node;
    if (!source || !parent || parent.destroyed) {
      this.detach(view);
      return null;
    }

    // A RESTING attach must have something to play. Hosting hides the sprite, so a symbol with no
    // idle loop (its skeleton lacks the clip, or its source sets `idle: false`) would be left showing
    // a static setup pose. Those cells stay plain sprites; `play()` still hosts them on demand.
    const idleClip = this.idleClipFor(symbolId);
    const restingWithNothingToPlay = (opts?.startIdle ?? true) && !opts?.poseClipName && !idleClip;
    if (restingWithNothingToPlay) {
      this.detach(view);
      return null;
    }

    const existing = this.hosted.get(view);
    if (existing && existing.symbolId === symbolId && !existing.spine.destroyed) {
      // Keep playing — no restart. The sprite still has to go back under the spine: the bind hook
      // re-shows it on EVERY bind.
      const sprite = view.symbolSprite;
      if (sprite && !sprite.destroyed) sprite.visible = false;
      existing.sprite = sprite;
      const spine = existing.spine;
      // Safety: a cell POSED for landing is frozen (timeScale 0). If it is re-bound as a RESTING
      // cell without ever having landed, roll it into idle so it doesn't sit on a still frame forever.
      if (!opts?.poseClipName && spine.state.timeScale === 0) {
        if (idleClip) {
          const entry = this.startClip(spine, idleClip, true);
          if (entry) entry.trackTime = Math.random() * (entry.animationEnd || 1);
        } else {
          spine.state.timeScale = 1;
        }
      }
      return spine;
    }
    this.detach(view);

    const spine = this.acquire(symbolId, source);

    // Scale 1 under the shared content parent: the art is authored 1:1 with the sprite natives.
    parent.addChild(spine);
    spine.position.set(source.offsetX ?? 0, source.offsetY ?? 0);
    spine.scale.set(source.scale, source.scale);
    spine.visible = true;
    applySpineClipMixes(spine, this.clipMix);

    const sprite = view.symbolSprite;
    if (sprite && !sprite.destroyed) sprite.visible = false;

    this.hosted.set(view, { spine, symbolId, sprite });

    // Posed pre-land: hold frame 0 of the land clip, frozen. `play()` later restores the time
    // scale and restarts the clip from the top.
    const poseClip = opts?.poseClipName;
    if (poseClip && this.hasClip(symbolId, poseClip)) {
      const posed = spine.state.setAnimation(0, poseClip, false);
      posed.trackTime = 0;
      spine.state.timeScale = 0;
      spine.update(0); // apply the pose now, so the first rendered frame is frame 0 (not setup)
      return spine;
    }

    // Ambient loop — SKIPPED when the caller is about to play something else (see play()).
    if ((opts?.startIdle ?? true) && idleClip) {
      const entry = this.startClip(spine, idleClip, true);
      // Desync the loops so a grid of idles doesn't pulse in unison.
      if (entry) entry.trackTime = Math.random() * (entry.animationEnd || 1);
      spine.update(0);
    }
    return spine;
  }

  /** Un-host a view: sprite restored, instance reclaimed. Safe on views that aren't hosted. */
  public detach(view: SymbolView): void {
    const cell = this.hosted.get(view);
    if (!cell) return;
    this.hosted.delete(view);
    if (cell.sprite && !cell.sprite.destroyed) cell.sprite.visible = true;
    this.release(cell.symbolId, cell.spine);
  }

  /**
   * Un-host a HIGHLIGHTED cell so sprite-based effects show through. Identical to detach — named
   * separately for intent; the cell re-hosts on its next resting bind.
   */
  public suspend(view: SymbolView | null): void {
    if (view) this.detach(view);
  }

  /** Un-host everything (recovery / teardown sweep). */
  public detachAll(): void {
    for (const view of Array.from(this.hosted.keys())) this.detach(view);
  }

  /** The live instance for a view, or null when it isn't hosted. */
  public getSkeleton(view: SymbolView | null): Spine | null {
    if (!view) return null;
    const cell = this.hosted.get(view);
    if (!cell || cell.spine.destroyed) return null;
    return cell.spine;
  }

  /** Number of hosted cells (tests / leak checks). */
  public getHostedCount(): number {
    return this.hosted.size;
  }

  // ── Playback ────────────────────────────────────────────────────────────────

  /**
   * Play `clipName` on this cell, hosting it first if needed. Returns the clip length in seconds
   * (UNSCALED — the caller waits on the runner, which applies the speed scaling), or 0 when the
   * cell can't play it so the caller can fall back. `queueIdleAfter` rolls a one-shot into the
   * ambient loop on the same skeleton.
   */
  public play(
    view: SymbolView,
    symbolId: string,
    clipName: string,
    opts?: { loop?: boolean; queueIdleAfter?: boolean },
  ): number {
    if (!this.hasClip(symbolId, clipName)) return 0;
    const spine = this.getSkeleton(view) ?? this.attach(view, symbolId, { startIdle: false });
    if (!spine || spine.destroyed) return 0;

    const entry = this.startClip(spine, clipName, opts?.loop ?? false);
    const idleClip = opts?.queueIdleAfter ? this.idleClipFor(symbolId) : null;
    if (idleClip) {
      // A track entry's speed multiplies the state's, which is still the one-shot's. Divide it back out
      // so the loop runs at its own speed (an ambient idle at 1), not at the speed of the clip it follows.
      const idle = spine.state.addAnimation(0, idleClip, true, 0);
      idle.timeScale = this.clipSpeed(idleClip) / spine.state.timeScale;
    }
    spine.update(0); // apply the new clip's first frame immediately (no one-frame setup-pose flash)
    const live = entry?.animationEnd ?? 0;
    if (live > 0) this.clipSecondsCache.set(`${symbolId}:${clipName}`, live);
    return live > 0 ? live : this.getClipSeconds(symbolId, clipName) || FALLBACK_CLIP_SECONDS;
  }

  /** Whether this symbol's skeleton carries `clipName` (cached; spine throws on unknown names). */
  public hasClip(symbolId: string, clipName: string): boolean {
    const key = `${symbolId}:${clipName}`;
    const cached = this.clipPresenceCache.get(key);
    if (cached !== undefined) return cached;
    const source = this.sourcesById.get(symbolId);
    if (!source) return false; // no entry yet — don't cache, the map may fill in later
    const animation = source.skeletonData.findAnimation(clipName);
    const present = !!animation;
    this.clipPresenceCache.set(key, present);
    if (present && animation && animation.duration > 0) this.clipSecondsCache.set(key, animation.duration);
    return present;
  }

  /**
   * Clip length in seconds, or 0 when unknown. Callers that merely need to WAIT should prefer the
   * value returned by `play()`; this is for querying ahead of playing (e.g. sizing a beat).
   */
  public getClipSeconds(symbolId: string, clipName: string): number {
    if (!this.hasClip(symbolId, clipName)) return 0;
    return this.clipSecondsCache.get(`${symbolId}:${clipName}`) ?? 0;
  }

  /** The idle loop this symbol rests on: none when the host has no idle clip, its source sets `idle: false` or its skeleton lacks the clip. */
  private idleClipFor(symbolId: string): string | null {
    const clip = this.idleClipName;
    if (!clip || !this.sourcesById.get(symbolId)?.idle || !this.hasClip(symbolId, clip)) return null;
    return clip;
  }

  /** Chrome vs round presentation — the one place this rule lives. */
  private clipSpeed(clipName: string): number {
    return this.ambientClips.has(clipName) ? 1 : this.taskRunner.getTimeScale();
  }

  private startClip(spine: Spine, clipName: string, loop: boolean): TrackEntry | null {
    // On the state, not the entry: spine advances a crossfade by the state's delta, so the mix into a
    // turbo clip keeps its pace (a super-turbo `out` lasts 0.08 s; a mix at 1 would take 0.12 s).
    spine.state.timeScale = this.clipSpeed(clipName);
    return spine.state.setAnimation(0, clipName, loop) ?? null;
  }

  private acquire(symbolId: string, source: SymbolSpineSource): Spine {
    const free = this.pool.get(symbolId);
    const pooled = free?.pop();
    if (pooled && !pooled.destroyed) {
      this.driver.attach(pooled);
      return pooled;
    }
    const spine = new Spine({ skeletonData: source.skeletonData, autoUpdate: false });
    spine.label = `SymbolSpine_${symbolId}`;
    this.driver.attach(spine);
    return spine;
  }

  private release(symbolId: string, spine: Spine): void {
    if (spine.destroyed) return;
    // Reset residue before pooling: a one-shot that ended all-invisible (a `win` explosion, an
    // `out`) would otherwise leak its end pose into the next reuse.
    spine.state.clearTracks();
    spine.skeleton.setToSetupPose();
    spine.state.timeScale = 1;
    spine.visible = false;
    spine.removeFromParent();
    this.driver.detach(spine);
    const free = this.pool.get(symbolId) ?? [];
    free.push(spine);
    this.pool.set(symbolId, free);
  }

  /** Destroy every instance, hosted and pooled (scene teardown). */
  public destroy(): void {
    this.detachAll();
    for (const free of this.pool.values()) {
      for (const spine of free) {
        this.driver.detach(spine);
        if (!spine.destroyed) spine.destroy();
      }
    }
    this.pool.clear();
  }
}
